import { create } from 'zustand';
import { fiscalMonthSettingsService } from '../services/fiscalMonthSettingsService';

export interface FiscalMonthSettings {
  startDay: number; // Day of month when fiscal month starts (1-31)
  enabled: boolean; // Whether to use fiscal months instead of calendar months
}

interface FiscalMonthSettingsState {
  settings: FiscalMonthSettings;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  hasLoadedFromAPI: boolean; // Track if we've loaded from API to prevent unnecessary reloads
  lastSavedAt: number | null; // Timestamp of last successful save to prevent stale reloads
  setSettings: (settings: Partial<FiscalMonthSettings>) => Promise<void>;
  resetToDefault: () => Promise<void>;
  loadSettings: (force?: boolean) => Promise<void>;
}

const defaultSettings: FiscalMonthSettings = {
  startDay: 1, // Default to calendar month (starts on 1st)
  enabled: false, // Default to calendar months
};

export const useFiscalMonthSettingsStore = create<FiscalMonthSettingsState>()((set, get) => ({
  settings: defaultSettings,
  isLoading: false as boolean,
  isSaving: false as boolean,
  error: null as string | null,
  hasLoadedFromAPI: false,
  lastSavedAt: null,
  
  // Load settings from API
  loadSettings: async (force = false) => {
    const state = get();
    
    // Skip if already loading
    if (state.isLoading) {
      return;
    }
    
    // Don't reload if we just saved recently (within last 2 seconds) unless forced
    // This prevents stale data from overwriting fresh updates
    if (!force && state.lastSavedAt && Date.now() - state.lastSavedAt < 2000) {
      console.log('Skipping loadSettings - recent save detected');
      return;
    }
    
    set({ isLoading: true, error: null });
    try {
      const settings = await fiscalMonthSettingsService.getSettings();
      set({ settings, isLoading: false, hasLoadedFromAPI: true });
    } catch (error: any) {
      console.error('Failed to load fiscal month settings:', error);
      set({ 
        error: error.message || 'Failed to load settings',
        isLoading: false,
        hasLoadedFromAPI: false // Allow retry on failure
      });
      // Keep using current settings (likely defaults) on error
    }
  },
  
  // Set settings and sync to API
  setSettings: async (newSettings: Partial<FiscalMonthSettings>) => {
    const currentSettings = get().settings;
    
    // Ensure we have a complete settings object
    // If newSettings is the full object, use it; otherwise merge with current
    const completeNewSettings: FiscalMonthSettings = {
      enabled: newSettings.enabled !== undefined ? newSettings.enabled : currentSettings.enabled,
      startDay: newSettings.startDay !== undefined ? newSettings.startDay : currentSettings.startDay,
    };
    
    console.log('[FiscalMonthStore] setSettings called', {
      newSettings,
      currentSettings,
      completeNewSettings,
    });
    
    // Optimistically update local state with complete settings
    set({ settings: completeNewSettings, isSaving: true, error: null });
    
    try {
      // Always include BOTH enabled and startDay in the update payload
      // Send the complete settings object to ensure nothing is lost
      const updatePayload: FiscalMonthSettings = {
        enabled: completeNewSettings.enabled,
        startDay: completeNewSettings.startDay,
      };
      
      console.log('[FiscalMonthStore] Sending to API', { updatePayload });
      
      // Sync to API - send complete settings object
      const savedSettings = await fiscalMonthSettingsService.updateSettings(updatePayload);
      
      console.log('[FiscalMonthStore] Received from API', { savedSettings });
      
      // Validate API response has all required fields
      if (savedSettings.enabled === undefined || savedSettings.startDay === undefined) {
        console.error('API response is missing required fields. Using optimistic update as fallback.', {
          savedSettings,
          updatePayload,
        });
        // Fall back to optimistic update if API response is incomplete
        set({ 
          settings: updatedSettings, 
          isSaving: false, 
          hasLoadedFromAPI: true,
          lastSavedAt: Date.now()
        });
        return;
      }
      
      // Use the saved settings from API as the source of truth
      // The API returns what was actually saved to the database
      const finalSettings: FiscalMonthSettings = {
        enabled: savedSettings.enabled,
        startDay: savedSettings.startDay,
      };
      
      // Validate that the API response matches what we sent
      // This helps catch any unexpected behavior or data loss
      if (newSettings.enabled !== undefined && savedSettings.enabled !== newSettings.enabled) {
        console.warn('API response does not match update request for enabled. Using API response as source of truth.', {
          requested: newSettings.enabled,
          received: savedSettings.enabled,
        });
      }
      
      if (newSettings.startDay !== undefined && savedSettings.startDay !== newSettings.startDay) {
        console.warn('API response does not match update request for startDay. Using API response as source of truth.', {
          requested: newSettings.startDay,
          received: savedSettings.startDay,
        });
      }
      
      set({ 
        settings: finalSettings, 
        isSaving: false, 
        hasLoadedFromAPI: true,
        lastSavedAt: Date.now() // Track when we last saved successfully
      });
    } catch (error: any) {
      console.error('Failed to save fiscal month settings:', error);
      // Revert to previous settings on error
      set({ 
        settings: currentSettings,
        error: error.message || 'Failed to save settings',
        isSaving: false 
      });
      throw error;
    }
  },
  
  resetToDefault: async () => {
    set({ isSaving: true, error: null });
    try {
      // Reset in API
      await fiscalMonthSettingsService.updateSettings(defaultSettings);
      set({ settings: defaultSettings, isSaving: false, hasLoadedFromAPI: true });
    } catch (error: any) {
      console.error('Failed to reset fiscal month settings:', error);
      set({ 
        error: error.message || 'Failed to reset settings',
        isSaving: false 
      });
      // Still update local state even if API fails
      set({ settings: defaultSettings });
    }
  },
}));

// Helper function to get fiscal month date range for a given date
export const getFiscalMonthRange = (
  date: Date,
  settings?: Partial<FiscalMonthSettings>
): { start: Date; end: Date } => {
  const currentSettings =
    settings || useFiscalMonthSettingsStore.getState().settings;

  if (!currentSettings.enabled) {
    // Use calendar month
    const start = new Date(date.getFullYear(), date.getMonth(), 1);
    const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    return { start, end };
  }

  const startDay = currentSettings.startDay ?? 1; // Default to 1 if undefined
  const currentDate = new Date(date);
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const day = currentDate.getDate();

  let fiscalStart: Date;
  let fiscalEnd: Date;

  if (day >= startDay) {
    // Fiscal month started this calendar month
    fiscalStart = new Date(year, month, startDay);
    // End is the day before startDay of next month
    const nextMonth = month + 1;
    fiscalEnd = new Date(year, nextMonth, startDay - 1);
  } else {
    // Fiscal month started last calendar month
    const prevMonth = month - 1;
    fiscalStart = new Date(year, prevMonth, startDay);
    // End is the day before startDay of this month
    fiscalEnd = new Date(year, month, startDay - 1);
  }

  return { start: fiscalStart, end: fiscalEnd };
};

// Helper function to get fiscal month number (1-12) for a given date
export const getFiscalMonthNumber = (
  date: Date,
  settings?: Partial<FiscalMonthSettings>
): number => {
  const currentSettings =
    settings || useFiscalMonthSettingsStore.getState().settings;

  if (!currentSettings.enabled) {
    return date.getMonth() + 1; // Calendar month (1-12)
  }

  const { start } = getFiscalMonthRange(date, settings);
  // Fiscal month is based on which calendar month the fiscal period starts in
  return start.getMonth() + 1;
};

